import { Component } from "react";
import { translate } from "@/components/IntlProviderWrapper";
import { getBackend } from "@/lib/backend/Backend2";
import Loader from "@/components/Loader";
import ErrorComponent from "@/components/ErrorComponent";
import ModelDetails from "./ModelDetails";

import { generateModelData, type ModelData } from "@/lib/ModelDetails";

const DEFAULT_ACTIVE_TAB = "modelProperties";

type Props = {
    materialExternalId: string;
    lineExternalId: string;
    processNum: string;
};

type State = {
    data?: ModelData;
    error?: string;
    isLoading: boolean;
    activeTab: string;
};

/**
 * Fetches model summary information and generates the model data displayed on the model details page.
 */
export default class Model extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            isLoading: false,
            activeTab: DEFAULT_ACTIVE_TAB,
        };
    }

    async componentDidMount() {
        await this.loadModelData();
    }

    async componentDidUpdate(prevProps: Props) {
        if (
            this.props.lineExternalId !== prevProps.lineExternalId ||
            this.props.materialExternalId !== prevProps.materialExternalId ||
            this.props.processNum !== prevProps.processNum
        ) {
            await this.loadModelData();
        }
    }

    setActiveTab = (tabKey: string | null) => {
        this.setState({ activeTab: tabKey || DEFAULT_ACTIVE_TAB });
    };

    async loadModelData() {
        const backend = getBackend();
        const { materialExternalId, lineExternalId, processNum } = this.props;

        this.setState({ isLoading: true, error: undefined });

        try {
            const modelSummary = await backend.models.modelSummary({
                line_external_id: lineExternalId,
                material_external_id: materialExternalId,
                process_num: processNum,
            });

            this.setState({
                data: generateModelData({
                    modelInfo: modelSummary.model_info,
                    points: modelSummary.input_data,
                    norms: modelSummary.norms,
                    evaluatedOperations: modelSummary.evaluated_operations,
                    material: modelSummary.material,
                }),
            });
        } catch (err: unknown) {
            this.setState({ error: (err as Error).message });
            console.log((err as Error).message);
        } finally {
            this.setState({ isLoading: false });
        }
    }

    render() {
        const { error, isLoading, data, activeTab } = this.state;

        if (isLoading) {
            return <Loader />;
        }

        if (error || !data) {
            return <ErrorComponent msg={translate("common.noData", "No data available")} type="warning" />;
        }

        return <ModelDetails model={data} activeTab={activeTab} setActiveTab={this.setActiveTab} />;
    }
}
